import { useState, useCallback, useContext } from 'react';
import { Location } from '../types/route';
import AuthContext from 'store/context/PublicContext';
import useCancelToken from 'hooks/useCancelToken';
import { post_middleware_osrm_search } from "services/custom_services"

interface UseLocationsReturn {
  locations: Location[];
  loading: boolean;
  error: string | null;
  searchLocations: (queryString: string) => Promise<void>;
  getCriteriaOptions: (level: number, parentValue?: string) => string[];
}

export const useLocations = (): UseLocationsReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { newCancelToken } = useCancelToken()
  const {
    user: { token },
  } = useContext(AuthContext)

  const searchLocations = useCallback(async (queryString: string) => {
    try {
      setLoading(true);
      setError(null);

      let _body = {
        from: 0,
        size: 100,
        queryString
      }
      
      const data: any = await post_middleware_osrm_search("locations", _body, token, newCancelToken());
      
      if (data?.data?.code === 200) {
        setLocations(data?.data?.message?.records);
      } else {
        throw new Error('Failed to fetch locations');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLocations([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCriteriaOptions = useCallback((level: number, parentValue?: string): string[] => {
    if (locations.length === 0) return [];

    let filteredLocations = locations;

    // Filter based on parent criteria
    if (level === 1 && parentValue) {
      filteredLocations = locations.filter(loc => loc.criteria0 === parentValue);
    } else if (level === 2 && parentValue) {
      filteredLocations = locations.filter(loc => 
        loc.criteria0 === locations.find(l => l.criteria1 === parentValue)?.criteria0 &&
        loc.criteria1 === parentValue
      );
    } else if (level === 3 && parentValue) {
      filteredLocations = locations.filter(loc => 
        loc.criteria2 === parentValue
      );
    }

    // Extract unique values for the current level
    const criteriaKey = `criteria${level}` as keyof Location;
    const options = filteredLocations
      .map(loc => loc[criteriaKey] as string)
      .filter((value, index, array) => value && array.indexOf(value) === index)
      .sort();

    return options;
  }, [locations]);

  return {
    locations,
    loading,
    error,
    searchLocations,
    getCriteriaOptions,
  };
};